import { makeStyles, tokens } from '@fluentui/react-components';
import { DEFAULT_ROW_HEIGHT, PROJECT_ROW_HEIGHT } from './hooks/useVirtualRows';

const projectRowMinHeight = `${PROJECT_ROW_HEIGHT}px`;
const taskRowMinHeight = `${DEFAULT_ROW_HEIGHT - 1}px`;
const customProjectRowColors = {
    darkBackground: '#181818',
    darkHover: '#141414',
    darkGridBorder: '#3f3f3f',
} as const;

export const useTimeGridStyles = makeStyles({
    pageShell: {
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        minWidth: 0,
    },

    pageContent: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '1400px',
        minWidth: 0,
        height: '100%',
    },

    // Toolbar above the grid
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },

    toolbarSection: {
        display: 'flex',
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: '0',
    },

    toolbarSectionEnd: {
        display: 'flex',
        justifyContent: 'flex-end',
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: '0',
    },

    // Grid container
    root: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        height: '100%',
        minHeight: 0,
        minWidth: 0,
        position: 'relative',
        color: tokens.colorNeutralForeground1,
        animationName: {
            from: { opacity: 0 },
            to: { opacity: 1 },
        },
        animationDuration: '150ms',
        animationTimingFunction: 'ease-in',
        animationFillMode: 'forwards',
    },

    // Single unified scroll container for both horizontal and vertical scrolling
    gridScrollContainer: {
        flexGrow: 1,
        minHeight: 0,
        minWidth: 0,
        overflowX: 'auto',
        overflowY: 'auto',
        position: 'relative',
    },

    gridContent: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 'min-content',
        minHeight: '100%',
    },

    // Header
    header: {
        display: 'grid',
        gridTemplateColumns: 'minmax(200px, 1fr) 200px repeat(7, 100px) 100px',
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderBottomColor: tokens.colorNeutralStroke2,
        backgroundColor: tokens.colorNeutralBackground1,
        position: 'sticky',
        top: '0',
        zIndex: 10,

        '@media (prefers-color-scheme: dark)': {
            borderBottomColor: customProjectRowColors.darkGridBorder,
        },
    },

    headerCell: {
        paddingTop: '8px',
        paddingBottom: '8px',
        paddingLeft: '12px',
        paddingRight: '12px',
        fontWeight: tokens.fontWeightBold,
        textAlign: 'center',
        fontSize: '13px',
        color: tokens.colorNeutralForeground2,
    },

    headerCellDateSub: {
        fontSize: '11px',
        fontWeight: tokens.fontWeightRegular,
        color: tokens.colorNeutralForeground3,
    },

    headerCellToday: {
        color: tokens.colorBrandForeground1,
    },

    headerCellTotal: {
        textAlign: 'right',
    },

    // Allocation variant — adds one extra column after the total
    headerWithAllocation: {
        gridTemplateColumns: 'minmax(200px, 1fr) 200px repeat(7, 100px) 100px 100px',
    },

    headerCellNonWorkday: {
        color: tokens.colorNeutralForeground3,
        backgroundColor: tokens.colorNeutralBackground2,
    },

    // Scroll / virtual containers
    scrollContainer: {
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: '0',
        minHeight: 0,
        overflowX: 'hidden',
        overflowY: 'auto',
        position: 'relative',
    },

    virtualContainer: {
        position: 'relative',
        width: '100%',
    },

    // Sticky total row — pinned to the bottom of the scroll container
    totalRowSticky: {
        position: 'sticky',
        bottom: '0',
        zIndex: 10,
    },

    // Sticky column cells — first column (project/task name)
    stickyCol1: {
        position: 'sticky',
        left: 0,
        zIndex: 2,
        backgroundColor: 'inherit',
    },

    // Sticky column cells — second column (work type)
    stickyCol2: {
        position: 'sticky',
        left: '200px',
        zIndex: 2,
        backgroundColor: 'inherit',
    },

    // Sticky column cells in header/total rows (corner intersections, needs higher z-index)
    stickyHeaderCol1: {
        position: 'sticky',
        left: 0,
        zIndex: 11,
        backgroundColor: 'inherit',
    },

    stickyHeaderCol2: {
        position: 'sticky',
        left: '200px',
        zIndex: 11,
        backgroundColor: 'inherit',
    },

    // Row base
    row: {
        display: 'grid',
        gridTemplateColumns: 'minmax(200px, 1fr) 200px repeat(7, 100px) 100px',
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderBottomColor: tokens.colorNeutralStroke2,
        alignItems: 'center',
        transitionProperty: 'background-color',
        transitionDuration: tokens.durationNormal,
        transitionTimingFunction: tokens.curveEasyEase,
        willChange: 'transform',

        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },

        '@media (prefers-color-scheme: dark)': {
            borderBottomColor: customProjectRowColors.darkGridBorder,
        },
    },

    // Row variants
    rowProject: {
        backgroundColor: tokens.colorNeutralBackground3,
        fontWeight: tokens.fontWeightSemibold,
        cursor: 'pointer',
        minHeight: projectRowMinHeight,

        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground3Hover,
        },

        '&:focus-visible': {
            outlineStyle: 'solid',
            outlineWidth: '2px',
            outlineColor: tokens.colorBrandStroke1,
            outlineOffset: '-2px',
        },

        '@media (prefers-color-scheme: dark)': {
            backgroundColor: customProjectRowColors.darkBackground,

            '&:hover': {
                backgroundColor: customProjectRowColors.darkHover,
            },
        },
    },

    rowTask: {
        backgroundColor: tokens.colorNeutralBackground1,
        minHeight: taskRowMinHeight,
    },

    rowTaskHighlighted: {
        backgroundColor: tokens.colorBrandBackground2,
    },

    rowWithAllocation: {
        gridTemplateColumns: 'minmax(200px, 1fr) 200px repeat(7, 100px) 100px 100px',
    },

    rowTotal: {
        backgroundColor: tokens.colorNeutralBackground3,
        fontWeight: tokens.fontWeightSemibold,
        borderTopWidth: '2px',
        borderTopStyle: 'solid',
        borderTopColor: tokens.colorNeutralStroke2,

        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground3Hover,
        },

        '@media (prefers-color-scheme: dark)': {
            backgroundColor: customProjectRowColors.darkBackground,
            borderTopColor: customProjectRowColors.darkGridBorder,

            '&:hover': {
                backgroundColor: customProjectRowColors.darkHover,
            },
        },
    },

    // Cell base
    cell: {
        display: 'flex',
        alignItems: 'center',
        // justifyContent: "center",
        // paddingTop: '6px',
        // paddingBottom: '6px',
        paddingLeft: '8px',
        paddingRight: '8px',
        overflowX: 'hidden',
        overflowY: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        minHeight: '36px',
    },

    // Cell variants
    cellProjectName: {
        display: 'flex',
        alignItems: 'center',
        columnGap: '8px',
        minWidth: 0,

        '& > span:first-child': {
            flexShrink: 0,
        },
    },

    cellTaskName: {
        display: 'flex',
        alignItems: 'center',
        columnGap: '8px',
        minWidth: 0,

        ':hover > .fui-trigger-pin': {
            opacity: 1,
            pointerEvents: 'auto',
        },
    },

    cellHours: {
        justifyContent: 'center',
        // textAlign: 'right',
        fontVariantNumeric: 'tabular-nums',
    },

    cellTotal: {
        justifyContent: 'center',
        // textAlign: 'right',
        fontWeight: tokens.fontWeightSemibold,
    },

    cellAllocation: {
        justifyContent: 'center',
        fontVariantNumeric: 'tabular-nums',
    },

    cellAllocationClickable: {
        cursor: 'pointer',

        ':hover': {
            textDecorationLine: 'underline',
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
    },

    cellTotalEditable: {
        justifyContent: 'flex-end',
        textAlign: 'right',
        fontWeight: tokens.fontWeightSemibold,
        position: 'relative',
        overflowX: 'visible',
        overflowY: 'visible',
    },

    // Non-workday column background (e.g. Saturday, Sunday)
    cellNonWorkday: {
        backgroundColor: tokens.colorNeutralBackground2,
    },

    cellEditable: {
        position: 'relative',
        overflowX: 'visible',
        overflowY: 'visible',

        ':focus-within > .fui-trigger-flag': {
            opacity: 1,
            pointerEvents: 'auto',
        },
        ':focus-within > .fui-trigger-comment': {
            opacity: 1,
            pointerEvents: 'auto',
        },
    },

    // Expand icon
    expandIcon: {
        transitionProperty: 'transform',
        transitionDuration: '300ms',
        transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
        transform: 'rotate(0deg)',
        fontSize: '12px',
    },

    expandIconExpanded: {
        transform: 'rotate(90deg)',
    },

    // Task indent spacer
    taskIndent: {
        width: '24px',
        flexShrink: 0,
        display: 'inline-block',
    },

    // Ellipsis text inside flex row headers
    ellipsisText: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        minWidth: 0,
    },

    // Task count badge
    taskCount: {
        flexShrink: 0,
        color: tokens.colorNeutralForeground3,
        fontSize: '0.9em',
        fontWeight: tokens.fontWeightRegular,
    },

    // Hours input — base state (no border)
    hoursInput: {
        width: '100%',
        backgroundColor: 'transparent',
        borderTopStyle: 'solid',
        borderRightStyle: 'solid',
        borderBottomStyle: 'solid',
        borderLeftStyle: 'solid',
        borderTopWidth: '1px',
        borderRightWidth: '1px',
        borderBottomWidth: '1px',
        borderLeftWidth: '1px',
        borderTopColor: 'transparent',
        borderRightColor: 'transparent',
        borderBottomColor: 'transparent',
        borderLeftColor: 'transparent',
        boxSizing: 'border-box',
        color: 'inherit',
        textAlign: 'center',
        height: '30px',
        // textAlign: 'right',
        // paddingTop: '4px',
        // paddingBottom: '4px',
        // paddingLeft: '8px',
        // paddingRight: '23px',

        // display: "flex",
        // alignItems: "center",
        // justifyContent: "center",

        borderTopLeftRadius: tokens.borderRadiusMedium,
        borderTopRightRadius: tokens.borderRadiusMedium,
        borderBottomLeftRadius: tokens.borderRadiusMedium,
        borderBottomRightRadius: tokens.borderRadiusMedium,
        fontVariantNumeric: 'tabular-nums',

        ':hover': {
            borderTopColor: tokens.colorNeutralStroke1Hover,
            borderRightColor: tokens.colorNeutralStroke1Hover,
            borderBottomColor: tokens.colorNeutralStroke1Hover,
            borderLeftColor: tokens.colorNeutralStroke1Hover,
        },

        ':focus': {
            outlineStyle: 'none',
            borderTopColor: tokens.colorBrandStroke1,
            borderRightColor: tokens.colorBrandStroke1,
            borderBottomColor: tokens.colorBrandStroke1,
            borderLeftColor: tokens.colorBrandStroke1,
            backgroundColor: tokens.colorNeutralBackground2,
        },
    },

    // Flag trigger button
    flagTrigger: {
        position: 'absolute',
        top: '50%',
        right: '13px',
        transform: 'translateY(-50%)',
        opacity: 0,
        pointerEvents: 'none',
        transitionProperty: 'opacity',
        transitionDuration: tokens.durationNormal,
        transitionTimingFunction: tokens.curveEasyEase,
        minWidth: '20px',
        width: '20px',
        height: '20px',
        paddingTop: '0',
        paddingBottom: '0',
        paddingLeft: '0',
        paddingRight: '0',
        zIndex: 1,
    },

    flagTriggerFlagged: {
        opacity: 1,
        pointerEvents: 'auto',
        color: tokens.colorPaletteRedForeground1,
    },

    // Comment trigger button
    commentTrigger: {
        position: 'absolute',
        top: '50%',
        left: '13px',
        transform: 'translateY(-50%)',
        opacity: 0,
        pointerEvents: 'none',
        transitionProperty: 'opacity',
        transitionDuration: tokens.durationNormal,
        transitionTimingFunction: tokens.curveEasyEase,
        minWidth: '20px',
        width: '20px',
        height: '20px',
        paddingTop: '0',
        paddingBottom: '0',
        paddingLeft: '0',
        paddingRight: '0',
        zIndex: 1,
    },

    commentTriggerHasComment: {
        opacity: 1,
        pointerEvents: 'auto',
    },

    // Comment textarea
    commentTextarea: {
        minWidth: '240px',

        '& textarea': {
            fieldSizing: 'content',
        },
    },

    // Total input — bold variant of hoursInput for the total cell
    totalInput: {
        fontWeight: tokens.fontWeightSemibold,
    },

    // Read-only hours input — dimmed, no hover/focus borders
    hoursInputReadOnly: {
        opacity: 0.7,
        cursor: 'default',

        ':hover': {
            borderTopColor: 'transparent',
            borderRightColor: 'transparent',
            borderBottomColor: 'transparent',
            borderLeftColor: 'transparent',
        },

        ':focus': {
            outlineStyle: 'none',
            borderTopColor: 'transparent',
            borderRightColor: 'transparent',
            borderBottomColor: 'transparent',
            borderLeftColor: 'transparent',
            backgroundColor: 'transparent',
        },
    },

    cellTotalLabel: {
        fontWeight: tokens.fontWeightMedium,
    },

    // Pin trigger button
    pinTrigger: {
        marginLeft: 'auto',
        flexShrink: 0,
        opacity: 0,
        pointerEvents: 'none',
        transitionProperty: 'opacity',
        transitionDuration: tokens.durationNormal,
        transitionTimingFunction: tokens.curveEasyEase,
        minWidth: '24px',
        width: '24px',
        height: '24px',
        paddingTop: '0',
        paddingBottom: '0',
        paddingLeft: '0',
        paddingRight: '0',
    },

    pinTriggerPinned: {
        opacity: 1,
        pointerEvents: 'auto',
        color: tokens.colorBrandForeground1,
    },
});
